import { getAuth } from '../../Config/matuAuth';
import { getDatabase, ref, get } from '../../Config/matuRtdb';

class SecurityMiddleware {
  constructor() {
    this.auth = getAuth();
    this.db = getDatabase();
    this.securityRules = {
      vipEndpoints: ['/vip', '/parlay', '/ia', '/premium'],
      publicEndpoints: ['/feed', '/marcador', '/public'],
      adminEndpoints: ['/admin', '/superadmin']
    };
  }

  // Verificar si el usuario está autenticado
  async isAuthenticated() {
    try {
      const user = this.auth.currentUser;
      return !!user;
    } catch (error) {
      console.error('Error verificando autenticación:', error);
      return false;
    }
  }

  // Verificar si el usuario es VIP
  async isVipUser(userId = null) {
    try {
      const uid = userId || this.auth.currentUser?.uid;
      if (!uid) return false;

      const userRef = ref(this.db, `users/${uid}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const userData = snapshot.val();
        
        // Múltiples verificaciones de VIP
        return (
          userData.vipStatus === "active" ||
          userData.plan === "vip" ||
          userData.isVip === true ||
          (userData.vipExpiry && userData.vipExpiry > Date.now()) ||
          userData.vipLevel === "premium" ||
          userData.subscriptionStatus === "active" ||
          userData.membership === "vip"
        );
      }
      return false;
    } catch (error) {
      console.error('Error verificando estado VIP:', error);
      return false;
    }
  }

  // Verificar si el usuario es admin
  async isAdminUser(userId = null) {
    try {
      const uid = userId || this.auth.currentUser?.uid;
      if (!uid) return false;

      const userRef = ref(this.db, `users/${uid}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const userData = snapshot.val();
        return (
          userData.role === "admin" ||
          userData.role === "superadmin" ||
          userData.isAdmin === true ||
          userData.adminLevel > 0
        );
      }
      return false;
    } catch (error) {
      console.error('Error verificando estado admin:', error);
      return false;
    }
  }

  // Validar acceso a endpoint
  async validateEndpointAccess(endpoint) {
    try {
      const isAuth = await this.isAuthenticated();
      
      // Endpoints públicos
      if (this.securityRules.publicEndpoints.some(ep => endpoint.includes(ep))) {
        return { allowed: true, reason: 'public' };
      }

      // Endpoints VIP
      if (this.securityRules.vipEndpoints.some(ep => endpoint.includes(ep))) {
        if (!isAuth) {
          return { allowed: false, reason: 'authentication_required', code: 401 };
        }
        
        const isVip = await this.isVipUser();
        if (!isVip) {
          return { allowed: false, reason: 'vip_required', code: 403 };
        }
        
        return { allowed: true, reason: 'vip_access' };
      }

      // Endpoints admin
      if (this.securityRules.adminEndpoints.some(ep => endpoint.includes(ep))) {
        if (!isAuth) {
          return { allowed: false, reason: 'authentication_required', code: 401 };
        }
        
        const isAdmin = await this.isAdminUser();
        if (!isAdmin) {
          return { allowed: false, reason: 'admin_required', code: 403 };
        }
        
        return { allowed: true, reason: 'admin_access' };
      }

      // Endpoints por defecto requieren autenticación
      if (!isAuth) {
        return { allowed: false, reason: 'authentication_required', code: 401 };
      }

      return { allowed: true, reason: 'authenticated' };
    } catch (error) {
      console.error('Error validando acceso:', error);
      return { allowed: false, reason: 'validation_error', code: 500 };
    }
  }

  // Interceptar peticiones HTTP
  async interceptRequest(url, options = {}) {
    const accessValidation = await this.validateEndpointAccess(url);
    
    if (!accessValidation.allowed) {
      throw new Error(`Acceso denegado: ${accessValidation.reason}`);
    }

    // Agregar headers de seguridad
    const secureOptions = {
      ...options,
      headers: {
        ...options.headers,
        'X-User-ID': this.auth.currentUser?.uid || '',
        'X-Access-Level': accessValidation.reason,
        'X-Timestamp': Date.now()
      }
    };

    return secureOptions;
  }

  // Validar datos de entrada
  validateInput(data, schema) {
    try {
      // Validación básica de esquema
      for (const [key, rules] of Object.entries(schema)) {
        if (rules.required && !data[key]) {
          throw new Error(`Campo requerido: ${key}`);
        }
        
        if (data[key] && rules.type && typeof data[key] !== rules.type) {
          throw new Error(`Tipo inválido para ${key}: esperado ${rules.type}`);
        }
        
        if (data[key] && rules.pattern && !rules.pattern.test(data[key])) {
          throw new Error(`Formato inválido para ${key}`);
        }
      }
      
      return { valid: true };
    } catch (error) {
      return { valid: false, error: error.message };
    }
  }

  // Sanitizar datos de entrada
  sanitizeInput(data) {
    const sanitized = {};
    
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === 'string') {
        // Remover scripts y HTML malicioso
        sanitized[key] = value
          .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
          .replace(/<[^>]*>/g, '')
          .trim();
      } else {
        sanitized[key] = value;
      }
    }
    
    return sanitized;
  }

  // Verificar rate limiting
  checkRateLimit(userId, action, limit = 100, windowMs = 60000) {
    const now = Date.now();
    const key = `rateLimit:${userId}:${action}`;
    
    // Implementar lógica de rate limiting
    // Por ahora retornamos true (sin límite)
    return { allowed: true, remaining: limit };
  }

  // Log de seguridad
  logSecurityEvent(event, details) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      event,
      details,
      userId: this.auth.currentUser?.uid || 'anonymous',
      userAgent: navigator.userAgent,
      ip: 'client-side' // En el cliente no tenemos IP real
    };

    console.log('Security Event:', logEntry);
    
    // Aquí podrías enviar el log a un servicio de monitoreo
    // this.sendToSecurityService(logEntry);
  }
}

// Instancia singleton
export const securityMiddleware = new SecurityMiddleware();

// Hooks de React para usar el middleware
export const useSecurityMiddleware = () => {
  return securityMiddleware;
};
